import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, PhoneOff, BadgeCheck, ShieldAlert, ShieldCheck, Ear, Tag, Building2, History, AlertTriangle, MessageSquareText, Quote, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneNumber } from '@/lib/utils';
import { formatPhone, phoneLocation } from '@/lib/phoneDisplay';
import { buildIncomingCard, lineLabel, initialsOf } from '@/lib/incomingCallCard';
import { screenCallStore } from '@/lib/screenCallStore';

interface IncomingCallCardProps {
  /** Use the LimePhone brand palette (mobile app) instead of the default emerald. */
  brand?: boolean;
  number: string;
  /** The org number that was dialled; shown as a line tag when it has a friendly name. */
  calledNumber?: string;
  insights?: any | null;
  fallbackName?: string;
  fallbackCompany?: string;
  onAccept: () => void;
  onDecline: () => void;
  className?: string;
}

const ROW_ICON = { tag: Tag, company: Building2, last: History } as const;

/**
 * Light incoming-call card (dashboard dialog and the /mobile overlay):
 * line tag, caller name + number (✓ for a known contact), up to three context
 * rows, spam warning, avatar on the right, then Accept / Decline.
 */
export function IncomingCallCard({ number, calledNumber, insights, fallbackName, fallbackCompany, onAccept, onDecline, className = '', brand = false }: IncomingCallCardProps) {
  const answerTone = brand
    ? 'bg-brand-call hover:bg-brand-call-hover focus-visible:ring-brand-call'
    : 'bg-emerald-500 hover:bg-emerald-600 focus-visible:ring-emerald-500';
  const sendTone = brand
    ? 'bg-brand-lime hover:bg-brand-lime-hover text-brand-ink'
    : 'bg-emerald-500 hover:bg-emerald-600 text-white';
  const { data: orgNumbers } = useQuery<any>({ queryKey: ['/api/organization/phone-numbers'], staleTime: 5 * 60 * 1000 });
  const numbers = Array.isArray(orgNumbers) ? orgNumbers : orgNumbers?.phoneNumbers || orgNumbers?.data || [];
  const line = lineLabel(calledNumber, numbers);
  const card = buildIncomingCard(number, insights, { name: fallbackName, company: fallbackCompany });
  // International grouping for E.164 numbers (+44 7944 645035), legacy mask otherwise.
  const pretty = number?.trim().startsWith('+') ? formatPhone(number) : formatPhoneNumber(number);
  const location = phoneLocation(number);
  const initials = initialsOf(card.name);
  const { toast } = useToast();
  const [replyOpen, setReplyOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [sending, setSending] = useState(false);

  // "Ask caller reason": the spoken name + reason, stored on the call row by
  // the inbound router. Polled briefly while the card rings.
  const { data: reasonData } = useQuery<{ reason: string | null }>({
    queryKey: ['/api/calls/caller-reason', number, calledNumber || ''],
    queryFn: async () => {
      const qs = new URLSearchParams({ from: number });
      if (calledNumber) qs.set('to', calledNumber);
      return (await apiRequest('GET', `/api/calls/caller-reason?${qs}`)) as { reason: string | null };
    },
    enabled: !!number,
    refetchInterval: (q: any) => (q?.state?.data?.reason ? false : 3000),
    retry: false,
  });
  const callerReason = reasonData?.reason || null;

  // Spam / STIR verdict for this ringing call (router writes it onto the call row).
  const { data: trust } = useQuery<{ callSid: string | null; spamSuspected: boolean; spamReason: string | null; verified: boolean }>({
    queryKey: ['/api/calls/incoming-trust', number, calledNumber || ''],
    queryFn: async () => {
      const qs = new URLSearchParams({ from: number });
      if (calledNumber) qs.set('to', calledNumber);
      return (await apiRequest('GET', `/api/calls/incoming-trust?${qs}`)) as any;
    },
    enabled: !!number,
    refetchInterval: (q: any) => (q?.state?.data?.callSid ? false : 2000),
    retry: false,
  });
  const suspectedSpam = !!trust?.spamSuspected || !!card.spam;
  const spamReason = trust?.spamReason || card.spam?.reason || null;
  const verifiedCaller = !suspectedSpam && !!trust?.verified;

  // "Screen call": the server answers with an assistant and redirects the caller
  // into screening; this leg ends and ScreenCallPanel takes over.
  const [screening, setScreening] = useState(false);
  const screenCall = async () => {
    if (!trust?.callSid || screening) return;
    setScreening(true);
    try {
      await apiRequest('POST', `/api/calls/${encodeURIComponent(trust.callSid)}/screen`);
      screenCallStore.start({ callSid: trust.callSid, number, calledNumber, name: card.name || undefined });
    } catch (e: any) {
      toast({ title: 'Could not screen this call', description: e?.message || 'Answer or decline instead.', variant: 'destructive' });
    } finally {
      setScreening(false);
    }
  };

  const { data: repliesData } = useQuery<{ replies: string[] }>({ queryKey: ['/api/user/quick-replies'], staleTime: 5 * 60 * 1000, enabled: replyOpen });
  const replies = repliesData?.replies || [];

  // Decline (the call goes to voicemail as on a normal decline), then text the
  // caller from the number they dialled via the regular SMS send path.
  const replyWith = async (text: string) => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    onDecline();
    try {
      await apiRequest('POST', '/api/sms/send', { to: number, ...(calledNumber ? { from: calledNumber } : {}), body });
      toast({ title: 'Message sent', description: body });
    } catch (e: any) {
      toast({ title: 'Call declined, but the text was not sent', description: e?.message || 'Please try again from Messages.', variant: 'destructive' });
    } finally {
      setSending(false);
      setReplyOpen(false);
    }
  };

  return (
    <div className={`w-full rounded-2xl bg-white text-slate-900 border shadow-[0_8px_30px_rgba(15,23,42,0.08)] dark:bg-card dark:text-foreground dark:shadow-none p-5 ${suspectedSpam ? 'border-red-300 ring-2 ring-red-500/60 dark:border-red-500/50' : 'border-slate-100 dark:border-border'} ${className}`} data-testid="incoming-call-card" data-spam={suspectedSpam ? 'true' : undefined}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {suspectedSpam ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-xs font-semibold" data-testid="tag-suspected-spam">
                <ShieldAlert className="h-3 w-3" aria-hidden />Suspected spam
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 px-2 py-0.5 text-xs font-semibold">
                <Phone className="h-3 w-3" aria-hidden />Incoming call
              </span>
            )}
            {verifiedCaller && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300 px-2 py-0.5 text-xs font-semibold" title="Caller ID verified by the carrier (STIR/SHAKEN A)" data-testid="tag-verified-caller">
                <ShieldCheck className="h-3 w-3" aria-hidden />Verified
              </span>
            )}
            {(line || calledNumber) && (
              <span className="rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 px-2 py-0.5 text-xs font-semibold truncate max-w-[12rem]" data-testid="tag-called-line">
                {line ? `via ${line}` : `To ${calledNumber!.trim().startsWith('+') ? formatPhone(calledNumber) : formatPhoneNumber(calledNumber!)}`}
              </span>
            )}
          </div>
          <h2 className="mt-2 text-2xl font-semibold leading-tight truncate" data-testid="text-caller-name">{card.name || pretty}</h2>
          <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500 dark:text-muted-foreground tabular-nums">
            {/* Under a name: the number. Under a bare number: its country, not the number again. */}
            {card.name ? pretty : (location || 'Unknown caller')}
            {card.verified && <BadgeCheck className="h-4 w-4 text-emerald-600" aria-label="Known contact" />}
          </p>
        </div>
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-slate-100 dark:bg-muted flex items-center justify-center text-xl font-semibold text-slate-600 dark:text-muted-foreground" aria-hidden>
          {card.avatarUrl ? <img src={card.avatarUrl} alt="" className="h-full w-full object-cover" /> : (initials || <Phone className="h-6 w-6 text-slate-400" />)}
        </div>
      </div>

      {suspectedSpam && (
        <div role="alert" className="mt-3 flex items-start gap-2.5 rounded-xl bg-red-600 px-3.5 py-3 text-white" data-testid="incoming-spam-warning">
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Suspected spam caller</p>
            {spamReason && <p className="text-xs text-white/85 truncate">{spamReason}</p>}
          </div>
        </div>
      )}

      {callerReason && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/20 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-200" data-testid="incoming-caller-reason">
          <Quote className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
          <span><span className="sr-only">Caller said: </span>{callerReason}</span>
        </div>
      )}

      {card.rows.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {card.rows.map((r) => {
            const Icon = ROW_ICON[r.kind];
            return (
              <li key={r.kind} className="flex items-center gap-2 text-sm text-slate-600 dark:text-muted-foreground">
                <Icon className="h-4 w-4 text-slate-400 shrink-0" aria-hidden />
                <span className="truncate">{r.text}</span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onDecline}
          aria-label="Decline call"
          className="h-12 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          data-testid="button-decline-call"
        >
          <PhoneOff className="h-5 w-5" aria-hidden />Decline
        </button>
        <button
          type="button"
          onClick={onAccept}
          aria-label="Accept call"
          autoFocus
          className={`h-12 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${answerTone}`}
          data-testid="button-answer-call"
        >
          <Phone className="h-5 w-5" aria-hidden />Accept
        </button>
      </div>
      {/* First incoming call: point out Screen call / Reply with message (no overlay, buttons stay live). */}

      {trust?.callSid && (
        <button
          type="button"
          onClick={screenCall}
          disabled={screening}
          className="mt-3 w-full h-10 rounded-2xl bg-amber-50 hover:bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200 dark:hover:bg-amber-500/25 text-sm font-medium flex items-center justify-center gap-2 transition disabled:opacity-60"
          data-testid="button-screen-call"
        >
          <Ear className="h-4 w-4" aria-hidden />{screening ? 'Screening…' : 'Screen call'}
        </button>
      )}

      {!replyOpen ? (
        <button
          type="button"
          onClick={() => setReplyOpen(true)}
          className="mt-3 w-full h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-muted dark:text-foreground dark:hover:bg-muted/80 text-sm font-medium flex items-center justify-center gap-2 transition"
          data-testid="button-reply-with-message"
        >
          <MessageSquareText className="h-4 w-4" aria-hidden />Reply with message
        </button>
      ) : (
        <div className="mt-3 rounded-2xl border border-slate-100 dark:border-border p-2" data-testid="reply-with-message-sheet">
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-xs font-semibold text-slate-500 dark:text-muted-foreground">Decline and text</span>
            <button type="button" aria-label="Close replies" onClick={() => setReplyOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-1.5">
            {replies.map((r, i) => (
              <button
                key={`${r}-${i}`}
                type="button"
                disabled={sending}
                onClick={() => replyWith(r)}
                className="w-full text-left rounded-xl bg-slate-50 hover:bg-emerald-50 dark:bg-muted dark:hover:bg-emerald-500/10 px-3 py-2 text-sm text-slate-700 dark:text-foreground transition disabled:opacity-60"
                data-testid={`button-quick-reply-${i}`}
              >
                {r}
              </button>
            ))}
            <form className="flex gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); replyWith(custom); }}>
              <input
                value={custom}
                maxLength={320}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom message…"
                className="flex-1 min-w-0 h-10 rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="input-custom-reply"
              />
              <button type="submit" disabled={sending || !custom.trim()} className={`h-10 px-3 rounded-xl text-sm font-semibold disabled:opacity-60 ${sendTone}`} data-testid="button-send-custom-reply">
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
